import {
  type GetPaginationSessionsParams,
  type SessionsOpportunity,
  useInfiniteScrollFromCache,
  useLazyGetSessionsOpportunitiesQuery,
} from '@carboncopyco/data';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
  useDashboardAnalytics,
} from '@carboncopyco/utils';
import { useEffect, useMemo, useState } from 'react';
import { SearchDropdown } from '../../../SearchDropdown';
import { useDashboardProps } from '../../DashboardContext';
import { useTableProps } from '../../DemoDashboard/SessionBreakdownTables/TableContext';
import { useFilterLogic } from './useFilterLogic';

export const OpportunitiesFilter = () => {
  const { organizationId, selectedWorkspaceIds, timeRange, short, internal } =
    useDashboardProps();
  const { opportunities, setOpportunities, tableTab } = useTableProps();
  const [query, setQuery] = useState('');
  const { trackDashboardSessionsBreakdownOpportunityFilterClick } =
    useDashboardAnalytics();

  const fetchArgs: GetPaginationSessionsParams = useMemo(
    () => ({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resourceTypes:
        tableTab === 'visits'
          ? ['shared_presentation']
          : ['managed_presentation', 'play'],
      query,
    }),
    [
      organizationId,
      short,
      internal,
      selectedWorkspaceIds,
      timeRange,
      tableTab,
      query,
    ]
  );

  const {
    items: data,
    isLoading,
    isLoadingBatch,
    fetchFirstBatch,
    fetchNextBatch,
    isEmpty,
  } = useInfiniteScrollFromCache<
    SessionsOpportunity,
    GetPaginationSessionsParams
  >(useLazyGetSessionsOpportunitiesQuery, fetchArgs);

  const [items, onChange] = useFilterLogic({
    data,
    selectedItems: opportunities,
    setter: setOpportunities,
    id: 'name',
    label: 'name',
  });

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs]);

  return (
    <SearchDropdown
      placeholder="Opportunity"
      capsuleWidth={208}
      contentWidth={208}
      searchableItems={items}
      selectedItems={opportunities}
      onChange={(item) => {
        onChange(item);
        trackDashboardSessionsBreakdownOpportunityFilterClick({});
      }}
      onClear={() => {
        setOpportunities({});
        setQuery('');
      }}
      query={query}
      setQuery={setQuery}
      onInputChange={fetchFirstBatch}
      fetchNextBatch={fetchNextBatch}
      isLoading={isLoading}
      isLoadingBatch={isLoadingBatch}
      disabled={isEmpty}
    />
  );
};
